import type { SessionLifecycleSnapshot } from '@adapters/controllers/SessionLifecycleController';
import { AppEntryGatewayError } from '@core/ports/AppEntryContextGateway';
import '@ui/components/organisms/SessionWarningModal';
import { createContainer, type SessionLifecycleControllerContract } from '@infrastructure/di/container';
import { LitElement, html } from 'lit';
import { customElement, query, state } from 'lit/decorators.js';
import '@ui/components/molecules/BackLink';
import '@ui/components/molecules/ErrorSummary';
import '@ui/components/organisms/NhsFooter';
import '@ui/components/organisms/NhsHeader';
import type { BackLinkViewModel } from '@ui/components/molecules/BackLink';
import type { FooterLinkViewModel } from '@ui/components/organisms/NhsFooter';
import { appShellTag } from '@ui/elementTags';
import { applyNhsGlobal } from '@ui/styles/applyNhsGlobal';
import appShellStyles from '@ui/styles/nhs-subsets/nhs-app-shell.scss?inline';

@customElement(appShellTag)
export class AppShell extends LitElement {
  private static readonly activityEventNames = ['pointerdown', 'keydown', 'input', 'change', 'submit'] as const;

  private readonly appEntryController = createContainer().appEntryController;

  @query('#router-outlet')
  private readonly outletElement?: HTMLElement;

  @state()
  private currentPath = globalThis.location.pathname;

  @state()
  private errorMessage = '';

  @state()
  private blockRouteContent = false;

  @state()
  private recoveryMessage = '';

  @state()
  private returnDestinations: FooterLinkViewModel[] = [];

  @state()
  private signedInDisplayName = '';

  @state()
  private showChrome = true;

  protected createRenderRoot(): HTMLElement | ShadowRoot {
    return applyNhsGlobal(this, appShellStyles);
  }

  public connectedCallback(): void {
    super.connectedCallback();
    globalThis.addEventListener('inav:route-changed', this.handleRouteChanged);
    AppShell.activityEventNames.forEach((eventName) => {
      globalThis.addEventListener(eventName, this.handleUserActivity, true);
    });
    this.initialize();
  }

  public disconnectedCallback(): void {
    globalThis.removeEventListener('inav:route-changed', this.handleRouteChanged);
    AppShell.activityEventNames.forEach((eventName) => {
      globalThis.removeEventListener(eventName, this.handleUserActivity, true);
    });
    this.unsubscribeSessionLifecycle?.();
    this.unsubscribeSessionLifecycle = undefined;
    super.disconnectedCallback();
  }

  private unsubscribeSessionLifecycle?: () => void;

  private currentSessionLifecycleController?: SessionLifecycleControllerContract;

  private sessionLifecycleSnapshot: SessionLifecycleSnapshot = { phase: 'idle' };

  public set sessionLifecycleController(controller: SessionLifecycleControllerContract | undefined) {
    this.currentSessionLifecycleController = controller;
    this.unsubscribeSessionLifecycle?.();
    this.unsubscribeSessionLifecycle = controller?.subscribe((snapshot) => {
      this.sessionLifecycleSnapshot = snapshot;
      this.requestUpdate();
    });
  }

  public render() {
    const backLinkViewModel: BackLinkViewModel = {
      href: '#',
      text: 'Back',
      visible: !this.blockRouteContent && this.showChrome && this.shouldShowBackLink(),
    };
    const canOpenProfile = this.returnDestinations.some((destination) => destination.id === 'profile');

    return html`
      ${this.showChrome
        ? html`
          <inav-nhs-header
            .accountLabel=${this.signedInDisplayName}
            .accountLinkVisible=${Boolean(this.signedInDisplayName && canOpenProfile)}
            @nhs-header-account=${this.handleAccountClick}
            @nhs-header-help=${this.handleHelp}></inav-nhs-header>
        `
        : null}
      <div class="nhsuk-width-container">
        <inav-back-link .viewModel=${backLinkViewModel} @back-link-click=${this.handleBackClick}></inav-back-link>
      </div>
      <main class="nhsuk-main-wrapper" id="router-wrapper" role="main">
        <div class="nhsuk-width-container">
          ${!this.blockRouteContent && this.recoveryMessage
            ? html`<div class="nhsuk-inset-text"><p class="nhsuk-body">${this.recoveryMessage}</p></div>`
            : null}
          <inav-error-summary .message=${this.errorMessage}></inav-error-summary>
          <div id="router-outlet" ?hidden=${this.blockRouteContent}></div>
        </div>
      </main>
      ${this.showChrome
        ? html`<inav-nhs-footer .links=${this.returnDestinations} @footer-link-click=${this.handleFooterLinkClick}></inav-nhs-footer>`
        : null}
      ${this.renderWarningModal()}
    `;
  }

  private renderWarningModal() {
    if (this.sessionLifecycleSnapshot.phase !== 'warning') {
      return null;
    }

    return html`
      <inav-session-warning-modal
        .onLogOut=${this.handleLogOut}
        .onStaySignedIn=${this.handleStaySignedIn}
        .viewModel=${{
          open: true,
          title: this.sessionLifecycleSnapshot.title ?? 'Session ending soon',
          message: this.sessionLifecycleSnapshot.message ?? 'Your session will end soon.',
          secondsRemaining: this.sessionLifecycleSnapshot.secondsRemaining
        }}
      ></inav-session-warning-modal>
    `;
  }

  public getOutlet(): HTMLElement {
    if (!this.outletElement) {
      throw new Error('Router outlet is not available.');
    }

    return this.outletElement;
  }

  private async initialize(): Promise<void> {
    try {
      const shellViewModel = await this.appEntryController.loadShell();
      this.showChrome = shellViewModel.showChrome;
      this.errorMessage = '';
      this.returnDestinations = shellViewModel.returnDestinations.map((destination) => ({
        id: destination.id,
        label: destination.label,
      }));
      this.recoveryMessage = shellViewModel.recoveryMessage ?? '';
      this.signedInDisplayName = shellViewModel.signedInDisplayName ?? '';
      this.blockRouteContent = false;
      await this.appEntryController.handleRouteChange(this.currentPath);
    } catch (error) {
      if (error instanceof AppEntryGatewayError && error.code === 'session-required') {
        if (this.shouldDeferSessionRequiredErrorToPage(this.currentPath)) {
          this.errorMessage = '';
          this.blockRouteContent = false;
        } else {
          this.errorMessage = error.message;
          this.blockRouteContent = true;
        }
      } else {
        this.errorMessage = error instanceof Error ? error.message : 'Unable to load the Intelligent Navigator shell.';
        this.blockRouteContent = false;
      }
    }
  }

  private readonly handleBackClick = async (event: Event): Promise<void> => {
    event.preventDefault();
    await this.appEntryController.handleBack(this.currentPath);
  };

  private readonly handleHelp = async (): Promise<void> => {
    await this.appEntryController.openHelp();
  };

  private readonly handleAccountClick = (): void => {
    void this.appEntryController.returnToApp('profile');
  };

  private readonly handleFooterLinkClick = (event: Event): void => {
    const footerEvent = event as CustomEvent<{ id: FooterLinkViewModel['id'] }>;
    void this.appEntryController.returnToApp(footerEvent.detail.id);
  };

  private readonly handleRouteChanged = async (event: Event): Promise<void> => {
    const routeEvent = event as CustomEvent<{ path: string }>;
    this.currentPath = routeEvent.detail.path;
    this.recoveryMessage = '';
    await this.appEntryController.handleRouteChange(this.currentPath);
  };

  private readonly handleStaySignedIn = (): void => {
    void this.currentSessionLifecycleController?.staySignedIn();
  };

  private readonly handleLogOut = (): void => {
    void this.currentSessionLifecycleController?.logOut();
  };

  private readonly handleUserActivity = (): void => {
    this.currentSessionLifecycleController?.recordActivity();
  };

  private shouldDeferSessionRequiredErrorToPage(path: string): boolean {
    return path === '/' || path === '/access-denied' || path === '/session-expired';
  }

  private shouldShowBackLink(): boolean {
    return this.appEntryController.shouldShowBackLink(this.currentPath);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [appShellTag]: AppShell;
  }
}